import React, { useEffect, useState } from 'react';
import DockVerticalDerecha from '../estructura/DockVerticalDerecha';
import { toast } from 'react-toastify';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import PlantillaModal from '../util/PlantillaModal';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import FuncionesGenerales from '../util/FuncionesGenerales';
import SpinnerCargando from '../util/SpinnerCargando';
import TablaPagoExterior from '../util/tablas/TablaPagoExterior';
import ModalPDFBase64 from '../util/ModalPDFBase64';
import { FaPlus, FaTrash } from 'react-icons/fa';

/**
 * Componente para gestionar Pagos al Exterior (E47)
 * Permite crear, ver y editar pagos con envío automático a DGII
 * 
 * E47 es para pagos a personas físicas o jurídicas no residentes fiscales
 * con retención total del ISR según Arts. 297 y 305 del Código Tributario.
 */
export default function ProcesoPagoExterior() {
  const { usuario } = useContextoSesion();
  const { obtenerFechaHoraActual, formatoMoneda } = FuncionesGenerales(usuario);

  // Estados principales
  const [formData, setFormData] = useState({
    detalles: [],
    identificador_extranjero: '',
    razon_social_beneficiario: '',
    pais_destino: '',
    concepto: '',
    tipo_moneda: 'DOP',
    tipo_cambio: null,
    saldo_anterior: null,
    monto_avance_pago: null
  });
  const [modo, setModo] = useState('creacion');
  const [editable, setEditable] = useState(true);
  const [cargando, setCargando] = useState(false);

  // Estados para modales
  const [mostrarModalPagos, setMostrarModalPagos] = useState(false);
  const [mostrarPDFPago, setMostrarPDFPago] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [cargandoPDF, setCargandoPDF] = useState(false);

  // Efecto: cambio de modo
  useEffect(() => {
    setEditable(modo === 'creacion');

    if (modo === 'ver') {
      toast.info('Modo de observación');
    }
    if (modo === 'creacion') {
      toast.info('Modo de creación');
    }
  }, [modo]);

  // Efecto: recalcular totales cuando cambian los detalles
  useEffect(() => {
    const totales = calcularTotales(formData.detalles);
    const saldoAnterior = parseFloat(formData.saldo_anterior || 0);
    const montoAvancePago = parseFloat(formData.monto_avance_pago || 0);
    const valorPagarXML = saldoAnterior !== 0 || montoAvancePago > 0 
      ? totales.monto_total + saldoAnterior - montoAvancePago 
      : totales.monto_total;
    
    setFormData(prev => ({
      ...prev,
      monto_total: totales.monto_total,
      isr_retenido_total: totales.isr_retenido_total,
      valor_neto_pagar: totales.valor_neto_pagar,
      valor_pagar_xml: valorPagarXML
    }));
  }, [formData.detalles, formData.saldo_anterior, formData.monto_avance_pago]);

  // Calcular totales
  const calcularTotales = (detalles) => {
    let monto_total = 0;
    let isr_retenido_total = 0;

    detalles.forEach(det => {
      const cantidad = parseFloat(det.cantidad) || 1;
      const precio = parseFloat(det.precio_unitario) || 0;
      const monto = parseFloat(det.monto_item) || (cantidad * precio);
      const isr = parseFloat(det.isr_retenido) || 0;

      monto_total += monto;
      isr_retenido_total += isr;
    });

    return {
      monto_total: parseFloat(monto_total.toFixed(2)),
      isr_retenido_total: parseFloat(isr_retenido_total.toFixed(2)),
      valor_neto_pagar: parseFloat((monto_total - isr_retenido_total).toFixed(2))
    };
  };

  // Handlers
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  function limpiarCampos() {
    setFormData({
      detalles: [],
      identificador_extranjero: '',
      razon_social_beneficiario: '',
      pais_destino: '',
      concepto: '',
      tipo_moneda: 'DOP',
      tipo_cambio: null,
      saldo_anterior: null,
      monto_avance_pago: null,
      tiene_e47: false,
      id_pago_exterior: null,
      ncf: null,
      dgii_track_id: null,
      fecha_hora: obtenerFechaHoraActual()
    });
    setPdfBase64(null);
    setModo('creacion');
    setEditable(true);
  }

  // =========================================================================
  // HANDLERS PARA DETALLES INLINE
  // =========================================================================

  const agregarLineaVacia = () => {
    if (!editable || formData.tiene_e47) return;

    // Verificar que todas las líneas existentes tengan datos mínimos
    const lineasIncompletas = formData.detalles.some(
      det => !det.descripcion || !det.monto_item || det.monto_item <= 0 || !det.isr_retenido || det.isr_retenido <= 0
    );

    if (lineasIncompletas && formData.detalles.length > 0) {
      toast.warning('Complete la línea anterior antes de agregar una nueva (descripción, monto e ISR retenido son obligatorios)');
      return;
    }

    setFormData(prev => ({
      ...prev,
      detalles: [...prev.detalles, {
        descripcion: '',
        cantidad: 1,
        precio_unitario: 0,
        monto_item: 0,
        isr_retenido: 0,
        indicador_facturacion: 4, // Exento
        indicador_bien_servicio: 2 // Servicio
      }]
    }));
  };

  const actualizarLinea = (index, campo, valor) => {
    if (!editable || formData.tiene_e47) return;

    setFormData(prev => {
      const nuevosDetalles = [...prev.detalles];
      nuevosDetalles[index] = { ...nuevosDetalles[index], [campo]: valor };

      // Recalcular monto_item si cambia cantidad o precio
      if (campo === 'cantidad' || campo === 'precio_unitario') {
        const cantidad = parseFloat(nuevosDetalles[index].cantidad) || 1;
        const precio = parseFloat(nuevosDetalles[index].precio_unitario) || 0;
        nuevosDetalles[index].monto_item = parseFloat((cantidad * precio).toFixed(2));
      }

      return { ...prev, detalles: nuevosDetalles };
    });
  };

  const eliminarLinea = (index) => {
    if (!editable || formData.tiene_e47) return;

    setFormData(prev => ({
      ...prev,
      detalles: prev.detalles.filter((_, i) => i !== index)
    }));
  };

  // =========================================================================
  // HANDLER PARA REENVIAR A DGII
  // =========================================================================

  const handleReenviarADGII = async () => {
    if (!formData.id_pago_exterior) {
      toast.error('No hay pago seleccionado');
      return;
    }

    if (formData.tiene_e47) {
      toast.info('Este pago ya fue enviado a DGII');
      return;
    }

    try {
      setCargando(true);

      const respuesta = await instanciaAxios.post(`/pago-exterior/${formData.id_pago_exterior}/enviar-dgii`);

      if (respuesta.data?.data?.pdf?.pdf_base64) {
        setPdfBase64(respuesta.data.data.pdf.pdf_base64);
        setMostrarPDFPago(true);
      }

      setFormData(prev => ({
        ...prev,
        tiene_e47: true,
        dgii_track_id: respuesta.data?.data?.dgii?.trackId
      }));

      toast.success('Pago enviado a DGII exitosamente');
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al reenviar a DGII:', error);
      toast.error(error.response?.data?.mensaje || 'Error al enviar a DGII');
    } finally {
      setCargando(false);
    }
  };

  // =========================================================================
  // HANDLER PARA VER PDF
  // =========================================================================

  const handleMostrarPDFPago = async () => {
    if (!formData.id_pago_exterior || !formData.tiene_e47) {
      toast.warning('El pago debe estar enviado a DGII para ver el PDF');
      return;
    }

    try {
      setCargandoPDF(true);
      const respuesta = await instanciaAxios.get(`/pago-exterior/${formData.id_pago_exterior}/pdf`);

      if (respuesta.data?.data?.pdf_base64) {
        setPdfBase64(respuesta.data.data.pdf_base64);
        setMostrarPDFPago(true);
      } else {
        toast.error('No se pudo obtener el PDF');
      }
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al obtener PDF:', error);
      toast.error('Error al obtener el PDF del pago');
    } finally {
      setCargandoPDF(false);
    }
  };

  // =========================================================================
  // HANDLER PARA GUARDAR
  // =========================================================================

  const handleGuardar = async () => {
    if (cargando) return;
    // Validaciones
    if (!formData.identificador_extranjero) {
      toast.error('El identificador del beneficiario extranjero es obligatorio');
      return;
    }

    if (!formData.razon_social_beneficiario) {
      toast.error('La razón social del beneficiario es obligatoria');
      return;
    }

    // Limpiar líneas vacías
    const detallesLimpios = formData.detalles.filter(
      det => det.descripcion && det.monto_item > 0 && det.isr_retenido > 0
    );

    if (detallesLimpios.length === 0) {
      toast.error('Debe agregar al menos un detalle con descripción, monto e ISR retenido');
      return;
    }

    // Validar ISR retenido total
    const totalISR = detallesLimpios.reduce((sum, d) => sum + parseFloat(d.isr_retenido || 0), 0);
    if (totalISR <= 0) {
      toast.error('E47 requiere ISR retenido mayor a 0');
      return;
    }

    try {
      setCargando(true);

      const dataEnviar = {
        identificador_extranjero: formData.identificador_extranjero,
        razon_social_beneficiario: formData.razon_social_beneficiario,
        pais_destino: formData.pais_destino || null,
        concepto: formData.concepto || null,
        fecha_hora: formData.fecha_hora || new Date().toISOString(),
        tipo_moneda: formData.tipo_moneda || 'DOP',
        tipo_cambio: formData.tipo_cambio || null,
        saldo_anterior: formData.saldo_anterior || null,
        monto_avance_pago: formData.monto_avance_pago || null,
        detalles: detallesLimpios
      };

      const respuesta = await instanciaAxios.post('/pago-exterior', dataEnviar);

      if (respuesta.data?.data?.pdf?.pdf_base64) {
        setPdfBase64(respuesta.data.data.pdf.pdf_base64);
        setMostrarPDFPago(true);
      }

      toast.success(respuesta.data.mensaje || 'Pago al exterior registrado exitosamente');

      // Actualizar formData con datos del nuevo pago
      setFormData(prev => ({
        ...prev,
        id_pago_exterior: respuesta.data.data.id_pago_exterior,
        ncf: respuesta.data.data.ncf,
        tiene_e47: !respuesta.data.data.dgii_error,
        dgii_track_id: respuesta.data.data.dgii?.trackId
      }));

      setModo('ver');
      setEditable(false);

    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al guardar pago exterior:', error);
      toast.error(error.response?.data?.mensaje || 'Error al registrar el pago al exterior');
    } finally {
      setCargando(false);
    }
  };

  // =========================================================================
  // HANDLER PARA SELECCIONAR PAGO DE LA TABLA
  // =========================================================================

  const handleSeleccionarPago = async (pago) => {
    try {
      setCargando(true);
      setMostrarModalPagos(false);

      const respuesta = await instanciaAxios.get(`/pago-exterior/${pago.id_pago_exterior}`);
      const pagoCompleto = respuesta.data;

      setFormData({
        ...pagoCompleto,
        detalles: pagoCompleto.detalles || []
      });

      setModo('ver');
      setEditable(false);
      setPdfBase64(null);

    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar pago:', error);
      toast.error('Error al cargar el pago seleccionado');
    } finally {
      setCargando(false);
    }
  };

  // =========================================================================
  // BOTONES DEL DOCK
  // =========================================================================

  const handleBotonNuevo = () => {
    limpiarCampos();
    setModo('creacion');
  };

  const handleBotonBuscar = () => {
    setMostrarModalPagos(true);
  };

  const handleBotonPDF = formData.tiene_e47 ? handleMostrarPDFPago : null;

  // =========================================================================
  // RENDER
  // =========================================================================

  if (cargando) {
    return <SpinnerCargando />;
  }

  return (
    <div className="flex flex-col md:flex-row gap-2 h-full">
      {/* Dock lateral */}
      <DockVerticalDerecha
        handleBotonCrear={handleBotonNuevo}
        handleBotonBuscar={handleBotonBuscar}
        handleBotonGuardar={editable ? handleGuardar : null}
        handleBotonPDF={handleBotonPDF}
      />

      {/* Contenido principal */}
      <div className="flex-1 bg-white/70 rounded-2xl shadow-lg p-4 md:p-6 overflow-auto">
        <h1 className="text-xl font-bold text-blue-800 mb-4 flex items-center gap-2">
          <span className="text-2xl">🌎</span>
          Pagos al Exterior (E47)
        </h1>

        {/* Indicadores de estado */}
        <div className="mb-4">
          {formData.tiene_e47 ? (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded-xl flex items-center gap-2">
              <span className="font-medium">✓ Enviado a DGII</span>
              {formData.dgii_track_id && (
                <span className="text-sm">- Track ID: {formData.dgii_track_id}</span>
              )}
            </div>
          ) : formData.id_pago_exterior && formData.ncf && !formData.tiene_e47 ? (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-2 rounded-xl flex items-center gap-2">
              <span className="font-medium">⚠ Pendiente de envío a DGII</span>
              <button
                onClick={handleReenviarADGII}
                className="ml-2 bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm"
              >
                Reenviar a DGII
              </button>
            </div>
          ) : null}
        </div>

        {/* Sección: Datos del Beneficiario */}
        <div className="mb-6 p-4 border border-blue-200 rounded-xl bg-blue-50/50">
          <h2 className="text-lg font-semibold text-blue-700 mb-3 flex items-center gap-2">
            <span>👤</span> Beneficiario Extranjero
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ID Fiscal Extranjero <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="identificador_extranjero"
                value={formData.identificador_extranjero || ''}
                onChange={handleChange}
                disabled={!editable}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Tax ID, Passport, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Razón Social <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="razon_social_beneficiario"
                value={formData.razon_social_beneficiario || ''}
                onChange={handleChange}
                disabled={!editable}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Nombre del beneficiario"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                País Destino
              </label>
              <input
                type="text"
                id="pais_destino"
                value={formData.pais_destino || ''}
                onChange={handleChange}
                disabled={!editable}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Ej: Estados Unidos"
              />
            </div>
          </div>
        </div>

        {/* Sección: Datos del Pago */}
        <div className="mb-6 p-4 border border-gray-200 rounded-xl">
          <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <span>💰</span> Datos del Pago
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Concepto
              </label>
              <input
                type="text"
                id="concepto"
                value={formData.concepto || ''}
                onChange={handleChange}
                disabled={!editable}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Descripción del pago"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Moneda
              </label>
              <select
                id="tipo_moneda"
                value={formData.tipo_moneda || 'DOP'}
                onChange={handleChange}
                disabled={!editable}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="DOP">DOP - Peso Dominicano</option>
                <option value="USD">USD - Dólar Estadounidense</option>
                <option value="EUR">EUR - Euro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Cambio {formData.tipo_moneda !== 'DOP' && <span className="text-red-500">*</span>}
              </label>
              <input
                type="number"
                id="tipo_cambio"
                value={formData.tipo_cambio || ''}
                onChange={handleChange}
                disabled={!editable || formData.tipo_moneda === 'DOP'}
                step="0.0001"
                min="0"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder={formData.tipo_moneda === 'DOP' ? 'N/A' : 'Ej: 58.5000'}
              />
            </div>
          </div>
          
          {/* Campos opcionales para ValorPagar */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-3 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Saldo Anterior (opcional)
              </label>
              <input
                type="number"
                id="saldo_anterior"
                value={formData.saldo_anterior || ''}
                onChange={handleChange}
                disabled={!editable}
                step="0.01"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500 mt-1">Se suma al total en el XML</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto Avance/Anticipo (opcional)
              </label>
              <input
                type="number"
                id="monto_avance_pago"
                value={formData.monto_avance_pago || ''}
                onChange={handleChange}
                disabled={!editable}
                step="0.01"
                min="0"
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="0.00"
              />
              <p className="text-xs text-gray-500 mt-1">Se resta del total en el XML</p>
            </div>
          </div>
          
          <div className="mt-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              NCF
            </label>
            <input
              type="text"
              value={formData.ncf || 'Se generará al guardar'}
              disabled
              className="w-full p-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
            />
          </div>
        </div>

        {/* Sección: Detalles del Pago */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <span>📋</span> Detalles del Pago
            </h2>
            {editable && !formData.tiene_e47 && (
              <button
                onClick={agregarLineaVacia}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
              >
                <FaPlus size={12} /> Agregar Línea
              </button>
            )}
          </div>

          {/* Tabla de detalles responsive */}
          <div className="overflow-x-auto border border-gray-200 rounded-xl">
            <table className="w-full min-w-[700px]">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">#</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Descripción</th>
                  <th className="p-3 text-center text-sm font-semibold text-gray-700 w-20">Cant.</th>
                  <th className="p-3 text-right text-sm font-semibold text-gray-700 w-28">Precio Unit.</th>
                  <th className="p-3 text-right text-sm font-semibold text-gray-700 w-28">Monto</th>
                  <th className="p-3 text-right text-sm font-semibold text-gray-700 w-28">ISR Retenido</th>
                  {editable && <th className="p-3 text-center text-sm font-semibold text-gray-700 w-16">Acción</th>}
                </tr>
              </thead>
              <tbody>
                {formData.detalles.length === 0 ? (
                  <tr>
                    <td colSpan={editable ? 7 : 6} className="p-6 text-center text-gray-500 italic">
                      No hay detalles. {editable && 'Haga clic en "Agregar Línea" para comenzar.'}
                    </td>
                  </tr>
                ) : (
                  formData.detalles.map((det, index) => (
                    <tr key={index} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="p-2 text-center text-gray-600">{index + 1}</td>
                      <td className="p-2">
                        <input
                          type="text"
                          value={det.descripcion || ''}
                          onChange={(e) => actualizarLinea(index, 'descripcion', e.target.value)}
                          disabled={!editable}
                          className="w-full p-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 text-sm"
                          placeholder="Descripción del servicio/pago"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="number"
                          value={det.cantidad || 1}
                          onChange={(e) => actualizarLinea(index, 'cantidad', parseFloat(e.target.value) || 1)}
                          disabled={!editable}
                          min="1"
                          step="1"
                          className="w-full p-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 text-sm text-center"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="number"
                          value={det.precio_unitario || ''}
                          onChange={(e) => actualizarLinea(index, 'precio_unitario', parseFloat(e.target.value) || 0)}
                          disabled={!editable}
                          min="0"
                          step="0.01"
                          className="w-full p-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 text-sm text-right"
                          placeholder="0.00"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="number"
                          value={det.monto_item || ''}
                          onChange={(e) => actualizarLinea(index, 'monto_item', parseFloat(e.target.value) || 0)}
                          disabled={!editable}
                          min="0"
                          step="0.01"
                          className="w-full p-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 text-sm text-right"
                          placeholder="0.00"
                        />
                      </td>
                      <td className="p-2">
                        <input
                          type="number"
                          value={det.isr_retenido || ''}
                          onChange={(e) => actualizarLinea(index, 'isr_retenido', parseFloat(e.target.value) || 0)}
                          disabled={!editable}
                          min="0"
                          step="0.01"
                          className="w-full p-1 border border-blue-300 rounded focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 text-sm text-right bg-blue-50"
                          placeholder="0.00"
                        />
                      </td>
                      {editable && (
                        <td className="p-2 text-center">
                          <button
                            onClick={() => eliminarLinea(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Eliminar línea"
                          >
                            <FaTrash size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sección: Totales */}
        <div className="p-4 border-2 border-gray-300 rounded-xl bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-sm text-gray-600">Monto Total</p>
              <p className="text-xl font-bold text-gray-800">
                {formatoMoneda(formData.monto_total || 0)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-blue-600 font-medium">ISR Retenido (Obligatorio)</p>
              <p className="text-xl font-bold text-blue-700">
                - {formatoMoneda(formData.isr_retenido_total || 0)}
              </p>
            </div>
            <div className="text-center bg-green-100 rounded-lg p-2">
              <p className="text-sm text-green-700 font-medium">Monto Después de ISR</p>
              <p className="text-xl font-bold text-green-800">
                {formatoMoneda(formData.valor_neto_pagar || 0)}
              </p>
              <p className="text-xs text-green-600 mt-1">
                (Total - ISR)
              </p>
            </div>
            
            {/* Mostrar ValorPagar XML si hay saldo o avance */}
            {(parseFloat(formData.saldo_anterior || 0) !== 0 || parseFloat(formData.monto_avance_pago || 0) > 0) && (
              <div className="text-center bg-purple-100 rounded-lg p-2">
                <p className="text-sm text-purple-700 font-medium">Valor a Pagar (XML)</p>
                <p className="text-xl font-bold text-purple-800">
                  {formatoMoneda(formData.valor_pagar_xml || formData.monto_total || 0)}
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  (Total + Saldo - Avance)
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Nota legal */}
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>Nota:</strong> E47 es para pagos a no residentes fiscales con retención obligatoria de ISR 
            según Arts. 297 y 305 del Código Tributario.
          </p>
        </div>
      </div>

      {/* Modal: Buscar Pagos */}
      <PlantillaModal
        mostrar={mostrarModalPagos}
        onClose={() => setMostrarModalPagos(false)}
        titulo="Buscar Pagos al Exterior"
      >
        <TablaPagoExterior
          handleSeleccionar={handleSeleccionarPago}
        />
      </PlantillaModal>

      {/* Modal: PDF */}
      <ModalPDFBase64
        mostrar={mostrarPDFPago}
        onClose={() => {
          setMostrarPDFPago(false);
          setPdfBase64(null);
        }}
        pdfBase64Directo={pdfBase64}
        ncf={formData.ncf}
        tituloDocumento="Pago al Exterior E47"
        tipoDocumento="pago_exterior"
      />
    </div>
  );
}

